import type {
  DirectorProviderConfigView,
  UpdateDirectorProviderConfigInput,
} from "@actale/contracts";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "./api.js";
import {
  CUSTOM_DIRECTOR_MODEL,
  DIRECTOR_MODEL_PRESETS,
  directorModelPreset,
} from "./director-model-presets.js";
import { DoubaoTtsProviderConfigPanel } from "./DoubaoTtsProviderConfigPanel.js";
import { DoubaoCharacterConfigPanel } from "./DoubaoCharacterConfigPanel.js";
import { OmniHumanProviderConfigPanel } from "./OmniHumanProviderConfigPanel.js";
import { SeedanceProviderConfigPanel } from "./SeedanceProviderConfigPanel.js";
import { formatUpdatedAt } from "./story-utils.js";

interface ProviderForm {
  modelName: string;
  reasoningEffort: DirectorProviderConfigView["reasoningEffort"];
  apiBaseUrl: string;
  apiKey: string;
  timeoutMs: number;
  fallbackEnabled: boolean;
  inputCostPerMillionUsd: number;
  outputCostPerMillionUsd: number;
}

function formFrom(data: DirectorProviderConfigView): ProviderForm {
  return {
    modelName: data.modelName,
    reasoningEffort: data.reasoningEffort,
    apiBaseUrl: data.apiBaseUrl,
    apiKey: "",
    timeoutMs: data.timeoutMs,
    fallbackEnabled: data.fallbackEnabled,
    inputCostPerMillionUsd: data.inputCostPerMillionUsd,
    outputCostPerMillionUsd: data.outputCostPerMillionUsd,
  };
}

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

function formatDuration(milliseconds: number): string {
  return milliseconds < 1_000 ? `${milliseconds} 毫秒` : `${(milliseconds / 1_000).toFixed(1)} 秒`;
}

export function DirectorProviderConfigPage(): React.ReactNode {
  const [data, setData] = useState<DirectorProviderConfigView | null>(null);
  const [form, setForm] = useState<ProviderForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [testConfirmed, setTestConfirmed] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [providerRefreshToken, setProviderRefreshToken] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.directorProviderConfig();
      setData(result);
      setForm(formFrom(result));
      setDeleteConfirm(false);
      setTestConfirmed(false);
    } catch (caught) {
      setError(message(caught, "无法读取 AI 模型设置"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!data || !form) return;
    if (!data.configured && !form.apiKey.trim()) {
      setError("首次配置必须填写 API Key");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const input: UpdateDirectorProviderConfigInput = {
        provider: "OPENAI_COMPATIBLE",
        modelName: form.modelName.trim(),
        reasoningEffort: form.reasoningEffort,
        apiBaseUrl: form.apiBaseUrl.trim(),
        timeoutMs: form.timeoutMs,
        fallbackEnabled: form.fallbackEnabled,
        inputCostPerMillionUsd: form.inputCostPerMillionUsd,
        outputCostPerMillionUsd: form.outputCostPerMillionUsd,
        expectedUpdatedAt: data.updatedAt,
        ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {}),
      };
      const result = await api.updateDirectorProviderConfig(input);
      setData(result);
      setForm(formFrom(result));
      setShowKey(false);
      setNotice("AI 模型设置已安全保存；全部真实 AI 开关已保持或恢复为关闭状态。");
    } catch (caught) {
      setError(message(caught, "AI 模型设置保存失败"));
    } finally {
      setSaving(false);
    }
  }

  async function testConnection(): Promise<void> {
    if (!data?.configured || !testConfirmed) return;
    setTesting(true);
    setError("");
    setNotice("");
    try {
      const result = await api.testDirectorProviderConfig();
      setNotice(`连接检查成功，用时 ${formatDuration(result.latencyMs)}${result.totalTokens == null ? "" : `，模型用量 ${result.totalTokens}`}。`);
      await load();
    } catch (caught) {
      const failure = message(caught, "AI 服务连接检查失败");
      await load();
      setError(failure);
    } finally {
      setTesting(false);
    }
  }

  async function remove(): Promise<void> {
    if (!deleteConfirm) return;
    setDeleting(true);
    setError("");
    setNotice("");
    try {
      if (!data?.updatedAt) return;
      await api.deleteDirectorProviderConfig(data.updatedAt);
      await load();
      setNotice("AI 模型设置和加密后的 API 密钥已删除，真实 AI 保持关闭。");
    } catch (caught) {
      setError(message(caught, "删除 AI 模型设置失败"));
    } finally {
      setDeleting(false);
    }
  }

  if (loading && !data) {
    return <div className="director-loading"><span /><strong>正在读取 AI 模型设置…</strong></div>;
  }
  if (!data || !form) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "AI 模型设置不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  const busy = saving || testing || deleting;
  const testStatus = data.lastTestStatus;
  const selectedPreset = directorModelPreset(form.modelName);
  const modelSelection = selectedPreset?.modelName ?? CUSTOM_DIRECTOR_MODEL;

  function selectModel(value: string): void {
    const preset = directorModelPreset(value);
    setForm((current) => {
      if (!current) return current;
      const currentPreset = directorModelPreset(current.modelName);
      if (!preset) {
        return {
          ...current,
          modelName: currentPreset ? "" : current.modelName,
          reasoningEffort: currentPreset ? null : current.reasoningEffort,
        };
      }
      return {
        ...current,
        modelName: preset.modelName,
        reasoningEffort: preset.reasoningEffort,
        timeoutMs: preset.timeoutMs,
        inputCostPerMillionUsd: preset.inputCostPerMillionUsd,
        outputCostPerMillionUsd: preset.outputCostPerMillionUsd,
      };
    });
  }

  return (
    <div className="provider-config-page">
      <div className="page-heading provider-config-heading">
        <div>
          <p className="eyebrow">AI 模型设置</p>
          <h1>AI 模型设置</h1>
          <p>设置故事导演和内容生成使用的 AI 服务。密钥只会加密保存在服务器中。</p>
        </div>
        <button className="secondary-button" disabled={busy || loading} onClick={() => { setProviderRefreshToken((current) => current + 1); void load(); }}>{loading ? "正在刷新…" : "刷新全部配置"}</button>
      </div>

      {error ? <div className="error-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice" role="status">✓ {notice}</div> : null}
      {!data.encryptionReady ? (
        <div className="provider-security-alert" role="alert">
          <strong>服务器加密主密钥尚未就绪</strong>
          <span>在服务器配置主密钥并重启 API 后，才能从后台保存供应商密钥。</span>
        </div>
      ) : null}

      <div className="provider-config-grid">
        <form className="provider-config-form" onSubmit={(event) => void save(event)}>
          <header>
            <div>
              <span className={`provider-state-dot ${data.configured ? "ready" : "empty"}`} />
              <div><strong>{data.configured ? "故事导演 AI 已设置" : "故事导演 AI 尚未设置"}</strong><small>保存设置不会自动允许使用真实 AI</small></div>
            </div>
            {data.apiKeyHint ? <span className="provider-key-hint">密钥 {data.apiKeyHint}</span> : null}
          </header>

          <div className="provider-form-grid">
            <label>
              <span>连接方式</span>
              <select value="OPENAI_COMPATIBLE" disabled><option value="OPENAI_COMPATIBLE">OpenAI 兼容接口</option></select>
            </label>
            <label>
              <span>故事导演模型</span>
              <select value={modelSelection} onChange={(event) => selectModel(event.target.value)}>
                {DIRECTOR_MODEL_PRESETS.map((preset) => (
                  <option key={preset.modelName} value={preset.modelName}>{preset.label}{preset.description ? ` · ${preset.description}` : ""}</option>
                ))}
                <option value={CUSTOM_DIRECTOR_MODEL}>自定义兼容模型</option>
              </select>
              {modelSelection === CUSTOM_DIRECTOR_MODEL ? (
                <input value={form.modelName} onChange={(event) => setForm({ ...form, modelName: event.target.value })} maxLength={120} placeholder="供应商提供的模型 ID" required />
              ) : <small>系统实际使用：{form.modelName}</small>}
            </label>
            <label className="provider-wide-field">
              <span>API 地址（高级设置）</span>
              <input type="url" value={form.apiBaseUrl} onChange={(event) => setForm({ ...form, apiBaseUrl: event.target.value })} maxLength={2048} placeholder="https://api.example.com/v1" required />
              <small>只允许 HTTPS 公网地址；本机、内网、带账号密码或重定向的地址会被拒绝。</small>
            </label>
            <label className="provider-wide-field">
              <span>{data.configured ? "API 密钥（留空表示不更换）" : "API 密钥"}</span>
              <div className="provider-secret-input">
                <input type={showKey ? "text" : "password"} value={form.apiKey} onChange={(event) => setForm({ ...form, apiKey: event.target.value })} autoComplete="new-password" minLength={8} maxLength={8192} placeholder={data.configured ? `已保存 ${data.apiKeyHint ?? "加密密钥"}` : "输入供应商 API Key"} required={!data.configured} />
                <button type="button" className="text-button" onClick={() => setShowKey(!showKey)}>{showKey ? "隐藏" : "显示"}</button>
              </div>
              <small>保存后页面不会再次获得或显示密钥明文。</small>
            </label>
            <label>
              <span>思考深度</span>
              <select
                value={form.reasoningEffort ?? ""}
                onChange={(event) => setForm({
                  ...form,
                  reasoningEffort: (event.target.value || null) as ProviderForm["reasoningEffort"],
                })}
                disabled={form.modelName === "gpt-4.1-mini"}
              >
                <option value="">自动（使用服务默认设置）</option>
                <option value="none">不启用（none）</option>
                <option value="low">较低（low）</option>
                <option value="medium">中等（medium）</option>
                <option value="high">高（high）</option>
                <option value="xhigh">极高（xhigh）</option>
                <option value="max">最高（max）</option>
              </select>
              <small>越高通常越稳，但等待更久，费用也可能更高。</small>
            </label>
            <label>
              <span>最长等待（秒）</span>
              <input type="number" min="1" max="120" step="1" value={form.timeoutMs / 1_000} onChange={(event) => setForm({ ...form, timeoutMs: Number(event.target.value) * 1_000 })} required />
            </label>
            <label className="provider-checkbox-field">
              <span>AI 服务失败时</span>
              <span><input type="checkbox" checked={form.fallbackEnabled} onChange={(event) => setForm({ ...form, fallbackEnabled: event.target.checked })} />使用备用导演继续故事</span>
            </label>
            <label>
              <span>输入单价（USD / 百万 Token）</span>
              <input type="number" min="0" max="1000" step="0.000001" value={form.inputCostPerMillionUsd} onChange={(event) => setForm({ ...form, inputCostPerMillionUsd: Number(event.target.value) })} required />
            </label>
            <label>
              <span>输出单价（USD / 百万 Token）</span>
              <input type="number" min="0" max="1000" step="0.000001" value={form.outputCostPerMillionUsd} onChange={(event) => setForm({ ...form, outputCostPerMillionUsd: Number(event.target.value) })} required />
            </label>
            <small className="provider-wide-field">选择官方预设会同步当前参考价格和推荐超时；保存前仍可人工调整。</small>
          </div>

          <div className="provider-save-warning">
            <span aria-hidden="true">!</span>
            <p><strong>修改设置会暂时停用所有真实 AI</strong><small>保存后先完成连接检查，再到“真实 AI 管理”重新开启后台试玩。</small></p>
          </div>
          <footer>
            <span>{data.updatedAt ? `最近更新：${formatUpdatedAt(data.updatedAt)} · ${data.updatedBy?.displayName ?? "系统"}` : "尚无已保存配置"}</span>
            <button className="primary-button compact" disabled={busy || !data.encryptionReady}>{saving ? "正在安全保存…" : "安全保存设置"}</button>
          </footer>
        </form>

        <aside className="provider-validation-panel">
          <header><strong>连接检查</strong><span className={testStatus === "SUCCESS" ? "success" : testStatus === "FAILED" ? "failed" : "untested"}>{testStatus === "SUCCESS" ? "已通过" : testStatus === "FAILED" ? "失败" : "未检查"}</span></header>
          <div className="provider-test-summary">
            <span>{testStatus === "SUCCESS" ? "✓" : testStatus === "FAILED" ? "!" : "○"}</span>
            <div>
              <strong>{testStatus === "SUCCESS" ? "AI 服务可以使用" : testStatus === "FAILED" ? "上次连接失败" : "等待首次连接检查"}</strong>
              <small>{data.lastTestedAt ? `${formatUpdatedAt(data.lastTestedAt)}${data.lastTestLatencyMs == null ? "" : ` · 用时 ${formatDuration(data.lastTestLatencyMs)}`}` : "保存设置后可以检查连接"}</small>
              {data.lastTestErrorCode ? <code>{data.lastTestErrorCode}</code> : null}
            </div>
          </div>
          <label className="provider-test-confirm">
            <input type="checkbox" checked={testConfirmed} onChange={(event) => setTestConfirmed(event.target.checked)} disabled={!data.configured} />
            <span>我确认检查会向 AI 服务发送一次最小请求，可能产生少量费用。</span>
          </label>
          <button className="secondary-button" type="button" disabled={busy || !data.configured || !testConfirmed} onClick={() => void testConnection()}>{testing ? "正在检查连接…" : "检查已保存设置"}</button>

          <section className="provider-security-notes">
            <strong>安全边界</strong>
            <ul>
              <li>API Key 使用 AES-256-GCM 加密</li>
              <li>页面与接口永不返回密钥明文</li>
              <li>拒绝本机、内网和非 HTTPS 地址</li>
              <li>保存、测试和删除均写入审计日志</li>
            </ul>
          </section>

          {data.configured ? (
            <div className="provider-delete-zone">
              <strong>删除配置</strong>
              <small>将删除数据库中的密文并关闭全部真实模型调用。</small>
              {!deleteConfirm ? <button type="button" className="danger-text-button" onClick={() => setDeleteConfirm(true)}>删除故事导演 AI 设置</button> : (
                <div><button type="button" className="danger-button" disabled={busy} onClick={() => void remove()}>{deleting ? "正在删除…" : "确认删除"}</button><button type="button" className="text-button" onClick={() => setDeleteConfirm(false)}>取消</button></div>
              )}
            </div>
          ) : null}
        </aside>
      </div>

      <DoubaoCharacterConfigPanel refreshToken={providerRefreshToken} />

      <div className="provider-section-heading">
        <div><strong>语音合成模型</strong><small>保存豆包语音合成凭据和默认参数；不会自动调用或切换现有角色试听。</small></div>
      </div>
      <DoubaoTtsProviderConfigPanel refreshToken={providerRefreshToken} />

      <div className="provider-section-heading">
        <div><strong>音频驱动人物视频模型</strong><small>保存 OmniHuman 1.5 的独立 AK/SK；不会上传素材、调用模型或产生费用。</small></div>
      </div>
      <OmniHumanProviderConfigPanel refreshToken={providerRefreshToken} />

      <div className="provider-section-heading">
        <div><strong>视频生成模型</strong><small>用于“视频生成”工作台，不影响故事导演模型。</small></div>
      </div>
      <SeedanceProviderConfigPanel refreshToken={providerRefreshToken} />
    </div>
  );
}
